import { useEffect, useRef, useState } from "react";
import { Megaphone, ChevronLeft, ChevronRight } from "lucide-react";
import MarkdownView from "@/components/MarkdownView";

const THEMES = {
  ivory: {
    bg: "bg-cream",
    border: "border-gold/40",
    title: "text-navy",
    body: "text-ink/80",
    eyebrow: "text-gold",
    icon: "bg-gold text-navy",
    cta: "bg-navy text-cream hover:bg-navy-900",
    dot: "bg-navy",
    dotInactive: "bg-navy/25",
    arrow: "bg-navy/5 text-navy hover:bg-navy/15",
  },
  gold: {
    bg: "bg-gold",
    border: "border-gold-600",
    title: "text-navy",
    body: "text-navy/85",
    eyebrow: "text-navy",
    icon: "bg-navy text-gold",
    cta: "bg-navy text-cream hover:bg-navy-900",
    dot: "bg-navy",
    dotInactive: "bg-navy/30",
    arrow: "bg-navy/10 text-navy hover:bg-navy/20",
  },
  navy: {
    bg: "bg-navy",
    border: "border-gold/30",
    title: "text-cream",
    body: "text-cream/85",
    eyebrow: "text-gold",
    icon: "bg-gold text-navy",
    cta: "bg-gold text-navy hover:bg-gold-600",
    dot: "bg-gold",
    dotInactive: "bg-cream/25",
    arrow: "bg-cream/10 text-cream hover:bg-cream/20",
  },
};

const AUTOPLAY_MS = 6500;

function Card({ data }) {
  const theme = THEMES[data.theme] || THEMES.ivory;
  return (
    <div
      className={`relative rounded-2xl ${theme.bg} ${theme.border} border-2 px-5 py-5 overflow-hidden shadow-lg shadow-black/10`}
      data-testid="prayer-announcement-card"
      data-announcement-id={data.id || "legacy"}
    >
      <div className="absolute inset-0 pattern-girih opacity-[0.08]" />
      <div className="relative flex flex-col md:flex-row gap-4 md:items-center">
        <div className={`shrink-0 w-11 h-11 rounded-2xl ${theme.icon} flex items-center justify-center`}>
          <Megaphone size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[10px] uppercase tracking-[0.25em] ${theme.eyebrow} font-semibold mb-1`}>Announcement</div>
          {data.title && (
            <div className={`font-serif text-xl md:text-2xl ${theme.title} leading-snug`}>{data.title}</div>
          )}
          {data.body && (
            <div className={`mt-1.5 text-sm ${theme.body} font-light`} style={{ lineHeight: 1.55 }}>
              <MarkdownView>{data.body}</MarkdownView>
            </div>
          )}
        </div>
        {data.cta_label && data.cta_url && (
          <a
            href={data.cta_url}
            target={data.cta_url.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            data-testid="prayer-announcement-cta"
            className={`self-start shrink-0 inline-flex items-center gap-2 rounded-full ${theme.cta} px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors`}
          >
            {data.cta_label}
          </a>
        )}
      </div>
    </div>
  );
}

// Accepts either an array via `items` or a single object via `data` (back-compat).
export default function PrayerAnnouncement({ items, data }) {
  // Normalise to array of enabled items with content
  const list = (() => {
    if (Array.isArray(items)) {
      return items.filter((i) => i && (i.title || i.body));
    }
    if (data && data.enabled && (data.title || data.body)) return [data];
    return [];
  })();

  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (list.length <= 1 || paused) return;
    timer.current = setTimeout(() => setIdx((i) => (i + 1) % list.length), AUTOPLAY_MS);
    return () => clearTimeout(timer.current);
  }, [idx, paused, list.length]);

  if (list.length === 0) return null;
  const current = list[Math.min(idx, list.length - 1)];
  const multi = list.length > 1;

  const go = (n) => setIdx((prev) => (n + list.length) % list.length);

  return (
    <div
      className="relative mb-6"
      data-testid="prayer-announcement-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      onMouseDown={() => setPaused(true)}
      onMouseUp={() => setPaused(false)}
    >
      <div className="relative">
        {/* animated key transition via inline class toggle */}
        <div key={current.id || idx} className="animate-fade-slide-in">
          <Card data={current} />
        </div>

        {multi && (
          <>
            <button
              type="button"
              aria-label="Previous announcement"
              onClick={() => go(idx - 1)}
              data-testid="ann-prev"
              className="absolute top-1/2 -translate-y-1/2 -left-2 md:-left-3 w-9 h-9 rounded-full bg-cream border-2 border-gold/40 text-navy hover:bg-gold hover:text-navy shadow-lg flex items-center justify-center transition-all"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              aria-label="Next announcement"
              onClick={() => go(idx + 1)}
              data-testid="ann-next"
              className="absolute top-1/2 -translate-y-1/2 -right-2 md:-right-3 w-9 h-9 rounded-full bg-cream border-2 border-gold/40 text-navy hover:bg-gold hover:text-navy shadow-lg flex items-center justify-center transition-all"
            >
              <ChevronRight size={16} />
            </button>
          </>
        )}
      </div>

      {multi && (
        <div className="flex items-center justify-center gap-1.5 mt-3" data-testid="ann-dots">
          {list.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to announcement ${i + 1}`}
              onClick={() => go(i)}
              data-testid={`ann-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-gold" : "w-1.5 bg-cream/30 hover:bg-cream/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
